/**
 * The Activity tab — Step 116, surfaced by Step 45.
 *
 * The audit rows have existed since Step 9 and are good; they simply never
 * reached the ticket, so an agent could not see what changed without the
 * admin audit page. This is that slice, merged server-side with the
 * conversation so the chronology is one list rather than two you have to
 * interleave in your head.
 *
 * Grouped by DAY, because "what happened on Tuesday" is the question people
 * actually bring to a timeline, and a flat list of forty timestamps answers
 * it worst.
 */
import { useQuery } from '@tanstack/react-query';
import { Bot, MessageSquare, PencilLine, StickyNote, UserCircle2 } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { apiFetch } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { cn } from '../../lib/utils';

interface Change {
  label: string;
  from?: string | null;
  to?: string | null;
  action?: string;
}
interface Entry {
  id: string;
  source: 'audit' | 'message';
  action: string;
  actorName: string | null;
  changes?: Change[];
  preview?: string;
  createdAt: number;
}

/** Reads as a sentence, because "updated · Group · Service Desk" is a data
 *  structure and this is meant to be read. The reference's own wording. */
function describe(e: Entry): string {
  if (e.source === 'message') {
    return e.action === 'private_note'
      ? 'Added a private note'
      : e.action === 'requester_message'
        ? 'Replied'
        : e.action === 'system'
          ? 'System message'
          : 'Sent a reply';
  }
  if (e.action === 'automation_ran') return 'Ran an automation';
  if (e.action === 'created') return 'Created the ticket';
  return 'Updated the ticket';
}

function iconFor(e: Entry) {
  if (e.action === 'automation_ran') return Bot;
  if (e.source === 'message') return e.action === 'private_note' ? StickyNote : MessageSquare;
  if (e.action === 'created') return UserCircle2;
  return PencilLine;
}

const DAY = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
const TIME = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

/** Step 114 — "2d 4h", "35m", "<1m": coarse on purpose; this is a timeline, not a stopwatch. */
function fmtDuration(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 1) return '<1m';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`;
  const d = Math.floor(h / 24);
  return `${d}d${h % 24 ? ` ${h % 24}h` : ''}`;
}

interface Transition {
  id: string;
  at: number;
  from: { id: string; name: string | null; category: string | null } | null;
  to: { id: string; name: string | null; category: string | null };
  via: string;
  actorName: string | null;
  durationMs: number | null;
}

const CATEGORY_TONE: Record<string, string> = {
  open: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  pending: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  resolved: 'bg-muted text-foreground',
  closed: 'bg-muted text-muted-foreground',
};

/**
 * Step 114 — the status timeline, from the transitions table rather than the
 * audit log: each hop shows how long the ticket sat in the state it left, and
 * the last chip shows time in the current state so far. Backfilled rows are
 * marked, because a reconstruction is not an observation.
 */
function StatusTimeline({ id }: { id: string }) {
  const q = useQuery({
    queryKey: qk.tickets.statusHistory(id),
    queryFn: () =>
      apiFetch<{ entries: Transition[]; currentSinceMs: number | null }>(`/api/tickets/${id}/status-history`),
    refetchInterval: 60_000,
  });
  if (q.isPending) return <Skeleton className="mx-4 mt-4 h-8" />;
  if (q.isError || !q.data || q.data.entries.length === 0) return null;
  const { entries, currentSinceMs } = q.data;
  return (
    <section className="border-b px-4 py-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</h3>
      <ol className="flex flex-wrap items-center gap-y-1 text-xs">
        {entries.map((t, i) => {
          const isLast = i === entries.length - 1;
          const tone = CATEGORY_TONE[t.to.category ?? ''] ?? 'bg-muted';
          const who =
            t.via === 'create'
              ? 'raised'
              : t.via === 'backfill'
                ? 'reconstructed'
                : t.actorName
                  ? `by ${t.actorName}`
                  : `by the ${t.via}`;
          return (
            <li key={t.id} className="flex items-center">
              {i > 0 && (
                <span
                  className="mx-1.5 text-muted-foreground"
                  title={
                    t.durationMs != null
                      ? `${fmtDuration(t.durationMs)} in ${t.from?.name ?? 'the previous status'}`
                      : undefined
                  }
                >
                  {t.durationMs != null && (
                    <span className="mr-1.5 tabular-nums">{fmtDuration(t.durationMs)}</span>
                  )}
                  →
                </span>
              )}
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 font-medium',
                  tone,
                  t.via === 'backfill' && 'border border-dashed',
                )}
                title={`${t.to.name ?? 'Unknown'} — ${fullTime(t.at)} · ${who}${t.via === 'backfill' ? ' (backfilled from the ticket record — intermediate moves are not recoverable)' : ''}`}
              >
                {t.to.name ?? 'Unknown'}
                {isLast && currentSinceMs != null && (
                  <span className="ml-1 font-normal text-muted-foreground">
                    · {fmtDuration(currentSinceMs)} so far
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function ActivityTab({ id }: { id: string }) {
  const q = useQuery({
    queryKey: qk.tickets.activity(id),
    queryFn: () => apiFetch<{ entries: Entry[] }>(`/api/tickets/${id}/activity`),
  });

  if (q.isError)
    return (
      <div className="m-4 space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load the activity</p>
        <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
          Retry
        </Button>
      </div>
    );
  if (q.isPending)
    return (
      <div className="space-y-3 p-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );

  const entries = q.data?.entries ?? [];
  if (!entries.length)
    return <p className="p-10 text-center text-sm text-muted-foreground">Nothing has happened yet.</p>;

  // Grouped in render order — the feed already arrives newest-first, so this
  // preserves it rather than re-sorting and risking a different answer.
  const days: { day: string; items: Entry[] }[] = [];
  for (const e of entries) {
    const day = DAY.format(new Date(e.createdAt));
    const last = days[days.length - 1];
    if (last && last.day === day) last.items.push(e);
    else days.push({ day, items: [e] });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <StatusTimeline id={id} />
      <div className="p-4">
        {days.map(({ day, items }) => (
          <section key={day} className="mb-5 last:mb-0">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {day}
            </h3>
            <ol className="space-y-1">
              {items.map((e) => {
                const Icon = iconFor(e);
                const system = !e.actorName;
                return (
                  <li key={e.id} className="flex gap-3 rounded-md px-2 py-1.5 hover:bg-accent/40">
                    <span
                      className={cn(
                        'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                        // The gear-equivalent: System is visually not a person,
                        // because "who did this?" is the first question and
                        // "nobody, it was a rule" is a real answer.
                        system ? 'bg-muted text-muted-foreground' : 'bg-brand/15 text-brand',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{e.actorName ?? 'System'}</span>{' '}
                        <span className="text-muted-foreground">{describe(e).toLowerCase()}</span>
                      </p>
                      {!!e.changes?.length && (
                        <ul className="mt-0.5 space-y-0.5">
                          {e.changes.map((c, i) => (
                            <li key={i} className="text-xs text-muted-foreground">
                              {c.action ? (
                                <>
                                  <span className="font-medium">{c.label}</span> {c.action}
                                </>
                              ) : (
                                <>
                                  Set <span className="font-medium">{c.label}</span> to{' '}
                                  <span className="text-foreground">{c.to ?? 'nothing'}</span>
                                  {c.from ? <span> (was {c.from})</span> : null}
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {e.preview && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{e.preview}</p>
                      )}
                    </div>
                    <time
                      className="shrink-0 text-xs tabular-nums text-muted-foreground"
                      title={fullTime(e.createdAt)}
                    >
                      {TIME.format(new Date(e.createdAt))}
                    </time>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}
